import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  inject,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';
import { ThemeService } from '../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

export interface LollipopChartData {
    label: string;
    value: number;
    color: string;
    type?: 'line' | 'bar';
}

@Component({
    selector: 'app-lollipop-chart',
    standalone: true,
    imports: [CommonModule],
    template: `<div #chartContainer class="lollipop-chart-container w-full h-full relative"></div>`,
    styles: [`
    .lollipop-chart-container {
      min-height: var(--min-height, 300px);
    }
    .lollipop-chart-tooltip {
        position: absolute;
        background: rgba(255, 255, 255, 0.96);
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 8px 12px;
        font-size: 11px;
        font-weight: 500;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.15s ease;
        z-index: 1000;
        color: #1e293b;
        min-width: 140px;
    }
    .dark .lollipop-chart-tooltip {
        background: rgba(30, 41, 59, 0.96);
        border-color: #334155;
        color: #f8fafc;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }
    .lollipop-chart-tooltip .tooltip-header {
        margin-bottom: 6px;
        padding-bottom: 6px;
        border-bottom: 1px solid #e2e8f0;
        font-weight: 600;
        color: #64748b;
    }
    .dark .lollipop-chart-tooltip .tooltip-header {
        border-color: #334155;
        color: #94a3b8;
    }
    .lollipop-chart-tooltip .tooltip-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-top: 4px;
    }
    .lollipop-chart-tooltip .tooltip-label {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #64748b;
    }
    .dark .lollipop-chart-tooltip .tooltip-label {
        color: #94a3b8;
    }
    .lollipop-chart-tooltip .tooltip-value {
        font-weight: 600;
        color: #1e293b;
    }
    .dark .lollipop-chart-tooltip .tooltip-value {
        color: #f8fafc;
    }
  `],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None
})
export class LollipopChartComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
    @Input() data: LollipopChartData[] = [];
    @Input() lineData: LollipopChartData[] = [];
    @Input() margin = { top: 40, right: 60, bottom: 40, left: 60 };
    @Input() scaleType: 'arithmetic' | 'logarithmic' = 'arithmetic';
    @Input() showLegend: boolean = true;
    @Input() showTooltip: boolean = true;
    @Input() legendLabel1: string = 'Series 1';
    @Input() legendLabel2: string = 'Series 2';
    @Input() minHeight: number = 300;
    @Input() legendOffset: number = 35;
    @Input() marginTop: number = 40;
    @Input() marginBottom: number = 40;
    @Input() hoverBarWidth: number = 60;
    @Input() badgeMinWidth: number = 60;
    @Input() valueFormat: 'number' | 'currency' | 'percentage' = 'currency';

    private themeService = inject(ThemeService);
    private resizeObserver!: ResizeObserver;
    private themeSubscription?: Subscription;
    private currentTheme: 'light' | 'dark' = 'dark';

    ngOnInit() {
        this.currentTheme = this.themeService.getTheme();
    }

    ngAfterViewInit() {
        this.resizeObserver = new ResizeObserver(() => this.drawChart());
        this.resizeObserver.observe(this.chartContainer.nativeElement);

        this.themeSubscription = this.themeService.theme$.subscribe(theme => {
            this.currentTheme = theme;
            this.drawChart();
        });
        
        setTimeout(() => this.drawChart(), 0);
    }

    ngOnChanges(changes: SimpleChanges) {
        if ((changes['data'] && !changes['data'].firstChange) || 
            (changes['lineData'] && !changes['lineData'].firstChange) ||
            (changes['scaleType'] && !changes['scaleType'].firstChange)) {
            this.drawChart();
        }
    }

    ngOnDestroy() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.themeSubscription) this.themeSubscription.unsubscribe();
    }

    private drawChart() {
        if (!this.data || this.data.length === 0) return;

        const element = this.chartContainer.nativeElement;
        element.style.setProperty('--min-height', `${this.minHeight}px`);
        d3.select(element).selectAll('*').remove();

        const finalMargin = { ...this.margin, top: this.marginTop, bottom: this.marginBottom };
        if (this.lineData && this.lineData.length > 0) {
            finalMargin.right = 60;
        } else {
            finalMargin.right = 40;
        }

        const width = element.clientWidth - finalMargin.left - finalMargin.right;
        const height = (element.clientHeight || this.minHeight) - finalMargin.top - finalMargin.bottom;

        if (width <= 0 || height <= 0) return;

        const container = d3.select(element).style('position', 'relative');
        const svg = container
            .append('svg')
            .attr('width', '100%')
            .attr('height', '100%')
            .attr('viewBox', `0 0 ${element.clientWidth} ${element.clientHeight || this.minHeight}`)
            .append('g')
            .attr('transform', `translate(${finalMargin.left},${finalMargin.top})`);

        const x = d3.scaleBand()
            .range([0, width])
            .domain(this.data.map(d => d.label))
            .padding(1);

        const yMax = d3.max(this.data, d => d.value) || 100;
        let y: d3.ScaleLinear<number, number> | d3.ScaleLogarithmic<number, number>;

        if (this.scaleType === 'logarithmic') {
            y = d3.scaleLog()
                .domain([1, yMax * 1.5])
                .range([height, 0]);
        } else {
            y = d3.scaleLinear()
                .domain([0, yMax * 1.2])
                .range([height, 0]);
        }

        let y2: d3.ScaleLinear<number, number> | null = null;
        if (this.lineData && this.lineData.length > 0) {
            const y2Max = d3.max(this.lineData, d => d.value) || 100;
            y2 = d3.scaleLinear()
                .domain([0, y2Max * 1.2])
                .range([height, 0]);
        }

        const themeColors = this.getThemeColors();

        // --- Y Axis Grid & Labels ---
        if (this.scaleType === 'logarithmic') {
            const logMin = Math.ceil(Math.log10(1));
            const logMax = Math.floor(Math.log10(yMax * 1.5));
            const powerTicks: number[] = [];
            for (let exp = logMin; exp <= logMax; exp++) {
                powerTicks.push(Math.pow(10, exp));
            }

            const yAxisGrid = svg.append('g');
            powerTicks.forEach(tickVal => {
                yAxisGrid.append('line')
                    .attr('x1', 0)
                    .attr('x2', width)
                    .attr('y1', (y as d3.ScaleLogarithmic<number, number>)(tickVal))
                    .attr('y2', (y as d3.ScaleLogarithmic<number, number>)(tickVal))
                    .attr('stroke', themeColors.gridLine)
                    .attr('stroke-dasharray', '3 3')
                    .attr('stroke-opacity', 0.5);
            });

            const yAxisLabels = svg.append('g').attr('class', 'y-axis');
            powerTicks.forEach(tickVal => {
                yAxisLabels.append('text')
                    .attr('x', -10)
                    .attr('y', (y as d3.ScaleLogarithmic<number, number>)(tickVal))
                    .attr('text-anchor', 'end')
                    .attr('dominant-baseline', 'middle')
                    .attr('fill', themeColors.axisText)
                    .style('font-size', '12px')
                    .text(
                        this.valueFormat === 'currency' ? `$${tickVal.toLocaleString()}` :
                        this.valueFormat === 'percentage' ? `${tickVal}%` :
                        `${tickVal.toLocaleString()}`
                    );
            });
        } else {
            const yAxisGrid = svg.append('g')
                .call(d3.axisLeft(y)
                    .ticks(5)
                    .tickSize(-width)
                    .tickFormat(() => '')
                );
            yAxisGrid.select('.domain').remove();
            yAxisGrid.selectAll('line')
                .attr('stroke', themeColors.gridLine)
                .attr('stroke-dasharray', '3 3')
                .attr('stroke-opacity', 0.5);

            const yAxis = svg.append('g')
                .attr('class', 'y-axis')
                .call(d3.axisLeft(y as d3.ScaleLinear<number, number>)
                    .ticks(5)
                    .tickFormat((d: any) => {
                        if (this.valueFormat === 'currency') return `$${d}`;
                        if (this.valueFormat === 'percentage') return `${d}%`;
                        return `${d}`;
                    })
                    .tickSize(0)
                );
            yAxis.select('.domain').remove();
            yAxis.selectAll('text')
                .attr('fill', themeColors.axisText)
                .style('font-size', '12px')
                .attr('x', -10);
        }

        const xAxis = svg.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(x).tickSize(0));

        xAxis.select('.domain').remove();
        xAxis.selectAll('text')
            .attr('transform', 'translate(0, 10)')
            .attr('fill', themeColors.axisText)
            .style('font-size', '12px');

        const barWidth = this.hoverBarWidth;
        this.data.forEach(d => {
            const xPos = x(d.label)! + x.bandwidth() / 2;
            const val = this.scaleType === 'logarithmic' && d.value < 1 ? 1 : d.value;
            const yPos = y(val as any);
            
            const group = svg.append('g').attr('class', 'data-group');

            const bgRect = group.insert('rect', ':first-child')
                .attr('class', 'bg-rect')
                .attr('x', xPos - barWidth / 2)
                .attr('y', yPos)
                .attr('width', barWidth)
                .attr('height', height - yPos)
                .attr('rx', 6)
                .attr('fill', d.color)
                .attr('opacity', 0);

            group.append('line')
                .attr('class', 'lollipop-stem')
                .attr('x1', xPos)
                .attr('x2', xPos)
                .attr('y1', height)
                .attr('y2', yPos)
                .attr('stroke', '#e5e7eb')
                .attr('stroke-width', 2);

            group.append('circle')
                .attr('class', 'lollipop-circle')
                .attr('cx', xPos)
                .attr('cy', yPos)
                .attr('r', 12)
                .attr('fill', d.color);
            
            const badgeGroup = group.append('g')
                .attr('class', 'label-group')
                .attr('opacity', 0)
                .style('pointer-events', 'none');

            let formattedValue = '';
            if (this.valueFormat === 'currency') {
                formattedValue = '$' + d.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            } else if (this.valueFormat === 'percentage') {
                formattedValue = d.value + '%';
            } else {
                 formattedValue = d.value.toLocaleString();
            }
            
            const approxTextWidth = formattedValue.length * 8; 
            const badgeWidth = Math.max(this.badgeMinWidth, approxTextWidth + 15);
            const badgeHeight = 28;
            const badgeRadius = 6;

            badgeGroup.append('rect')
                .attr('x', xPos - badgeWidth / 2)
                .attr('y', yPos - badgeHeight - 5) 
                .attr('width', badgeWidth)
                .attr('height', badgeHeight)
                .attr('rx', badgeRadius)
                .attr('fill', d.color)
                .attr('stroke', '#fff')
                .attr('stroke-width', 0)
                .attr('filter', 'drop-shadow(0px 2px 2px rgba(0,0,0,0.1))');

            badgeGroup.append('text')
                .attr('x', xPos)
                .attr('y', yPos - badgeHeight / 2) 
                .attr('text-anchor', 'middle')
                .attr('dominant-baseline', 'middle')
                .text(formattedValue)
                .attr('fill', '#ffffff')
                .style('font-size', '12px')
                .style('font-weight', '700');

            group.append('rect')
                .attr('x', xPos - barWidth / 2)
                .attr('y', 0)
                .attr('width', barWidth)
                .attr('height', height)
                .attr('fill', 'transparent')
                .style('cursor', 'pointer')
                .on('mouseenter', (event) => {
                    const g = d3.select(event.currentTarget.parentNode as Element);
                    
                    g.select('.bg-rect')
                        .transition().duration(200)
                        .attr('opacity', 0.15);

                    g.select('.label-group')
                        .transition().duration(200)
                        .attr('opacity', 1)
                        .attr('transform', 'translate(0, -5)');
                    
                    g.select('.lollipop-circle')
                        .transition().duration(200)
                        .attr('opacity', 0);

                    if (this.showTooltip) {
                        const lineItem = this.lineData[this.data.indexOf(d)];
                        const lineValue = lineItem ? lineItem.value : null;
                        const lineLabel = this.legendLabel2;
                        const barLabel = this.legendLabel1;

                        const tooltipHtml = `
                            <div style="display:flex;flex-direction:column;gap:4px;">
                                <div style="font-weight:600;color:${themeColors.text};margin-bottom:4px;">${d.label}</div>
                                <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
                                    <span style="font-size:12px;color:${themeColors.axisText};display:flex;align-items:center;gap:6px;">
                                        <span style="width:8px;height:8px;border-radius:50%;background-color:${d.color}"></span>
                                        ${barLabel}
                                    </span>
                                    <span style="font-weight:600;font-size:13px;color:${themeColors.text};">
                                        ${d.value.toLocaleString()}
                                    </span>
                                </div>
                                ${lineItem ? `
                                <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
                                    <span style="font-size:12px;color:${themeColors.axisText};display:flex;align-items:center;gap:6px;">
                                         <span style="width:8px;height:8px;border-radius:50%;background-color:${lineItem.color}"></span>
                                        ${lineLabel}
                                    </span>
                                    <span style="font-weight:600;font-size:13px;color:${themeColors.text};">
                                        ${lineItem.value}%
                                    </span>
                                </div>` : ''}
                            </div>
                        `;

                       let tooltip: any = d3.select('body').select('.lollipop-tooltip');
                       
                       if (tooltip.empty()) {
                           tooltip = d3.select('body').append('div')
                               .attr('class', 'lollipop-tooltip')
                               .style('position', 'absolute')
                               .style('background', themeColors.background)
                               .style('border', `1px solid ${themeColors.gridLine}`)
                               .style('border-radius', '8px')
                               .style('padding', '8px 12px')
                               .style('box-shadow', '0 4px 6px -1px rgba(0, 0, 0, 0.1)')
                               .style('pointer-events', 'none')
                               .style('z-index', '9999')
                               .style('font-size', '12px')
                               .style('font-family', 'Inter, sans-serif')
                               .style('opacity', 0)
                               .style('left', '-9999px')
                               .style('top', '-9999px');
                       }
                       
                       tooltip.html(tooltipHtml)
                           .style('opacity', 1)
                           .style('left', (event.pageX + 15) + 'px')
                           .style('top', (event.pageY - 10) + 'px');
                    }
                })
                .on('mousemove', (event) => {
                     if (this.showTooltip) {
                         const tooltip = d3.select('body').select('.lollipop-tooltip');
                         if (!tooltip.empty()) {
                             tooltip
                                .style('left', (event.pageX + 15) + 'px')
                                .style('top', (event.pageY - 10) + 'px');
                         }
                     }
                })
                .on('mouseleave', (event) => {
                    const g = d3.select(event.currentTarget.parentNode as Element);

                    g.select('.bg-rect')
                        .transition().duration(200)
                        .attr('opacity', 0);

                    g.select('.label-group')
                        .transition().duration(200)
                        .attr('opacity', 0)
                        .attr('transform', 'translate(0, 0)');
                        
                    g.select('.lollipop-circle')
                        .transition().duration(200)
                        .attr('opacity', 1);
                    
                    if (this.showTooltip) {
                        d3.select('body').select('.lollipop-tooltip').style('opacity', 0).remove();
                    }
                });
        });

        if (this.lineData && this.lineData.length > 0 && y2) {
            const line = d3.line<LollipopChartData>()
                .x(d => x(d.label)! + x.bandwidth() / 2)
                .y(d => y2!(d.value))
                .curve(d3.curveMonotoneX);

            svg.append('path')
                .datum(this.lineData)
                .attr('fill', 'none')
                .attr('stroke', this.lineData[0].color)
                .attr('stroke-width', 2)
                .attr('d', line);

            this.lineData.forEach(d => {
                const xPos = x(d.label)! + x.bandwidth() / 2;
                const yPos = y2!(d.value);

                svg.append('circle')
                    .attr('cx', xPos)
                    .attr('cy', yPos)
                    .attr('r', 5)
                    .attr('fill', d.color)
                    .attr('stroke', themeColors.background)
                    .attr('stroke-width', 2);
            });
            
            const yAxis2 = svg.append('g')
                .attr('transform', `translate(${width}, 0)`)
                .call(d3.axisRight(y2)
                    .ticks(5)
                    .tickSize(0)
                    .tickFormat(d => `${d}%`)
                );
            yAxis2.select('.domain').remove();
            yAxis2.selectAll('text')
                .attr('fill', themeColors.axisText)
                .style('font-size', '12px')
                .attr('x', 10);
        }

        if (this.showLegend) {
            const legendGroup = svg.append('g')
                .attr('transform', `translate(${width / 2}, ${height + 35})`);
            
            let currentX = 0;
            const itemGap = 30;

            if (this.data.length > 0) {
                 const g1 = legendGroup.append('g');
                 g1.append('circle').attr('r', 5).attr('fill', this.data[0].color);
                 const t1 = g1.append('text').attr('x', 10).attr('y', 4).text(this.legendLabel1)
                    .style('font-size', '12px').attr('fill', themeColors.legendText);
                 
                 const w1 = (t1.node() as SVGTextElement | null)?.getComputedTextLength() || 0;
                 currentX += 10 + w1 + itemGap;
            }

            if (this.lineData.length > 0) {
                 const g2 = legendGroup.append('g').attr('transform', `translate(${currentX}, 0)`);
                 g2.append('circle').attr('r', 5).attr('fill', this.lineData[0].color);
                 const t2 = g2.append('text').attr('x', 10).attr('y', 4).text(this.legendLabel2)
                    .style('font-size', '12px').attr('fill', themeColors.legendText);
                 
                 const w2 = (t2.node() as SVGTextElement | null)?.getComputedTextLength() || 0;
                 currentX += 10 + w2;
            }
            
            legendGroup.attr('transform', `translate(${width/2 - currentX/2}, ${height + this.legendOffset})`);
        }
    }

    private getThemeColors() {
        const isDark = this.currentTheme === 'dark';
        return {
            axisText: isDark ? '#a1a1aa' : '#6b7280',
            gridLine: isDark ? '#2e2e33' : '#e5e7eb',
            text: isDark ? '#ffffff' : '#374151',
            background: isDark ? '#1e1e2d' : '#ffffff',
            legendText: isDark ? '#a1a1aa' : '#6b7280'
        };
    }
}
